import { router } from "@inertiajs/react";
import { ArrowDown, LoaderIcon, UserCheck, UserX } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { getCsrfToken } from "@/lib/csrf";
import type { UserSearchCandidate } from "@/types/admin-portal/patient";
import { AsyncSearchCombobox } from "./async-search-combobox";

export interface ChangeOwnerDrawerProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	patientId: string;
	patientName: string;
	patientNumber?: string;
	patientEmail?: string | null;
	hasUserAccount: boolean;
	orderCount?: number;
}

export default function ChangeOwnerDrawer({
	open,
	onOpenChange,
	patientId,
	patientName,
	patientNumber,
	patientEmail,
	hasUserAccount,
	orderCount = 0,
}: ChangeOwnerDrawerProps) {
	const { t: td } = useTranslation("translation", { keyPrefix: "components" });
	const { t } = useTranslation("patients", { keyPrefix: "change_owner" });
	const [selected, setSelected] = useState<UserSearchCandidate | null>(null);
	const [reassignOrders, setReassignOrders] = useState(true);
	const [reason, setReason] = useState("");
	const [loading, setLoading] = useState(false);

	function reset() {
		setSelected(null);
		setReassignOrders(true);
		setReason("");
	}

	async function onSubmit() {
		if (!selected) return;
		setLoading(true);
		try {
			const res = await fetch(
				`/admin-portal/patient-management/${patientId}/owner`,
				{
					method: "PATCH",
					headers: {
						"Content-Type": "application/json",
						Accept: "application/json",
						"X-CSRF-Token": getCsrfToken(),
					},
					body: JSON.stringify({
						user_id: selected.id,
						reassign_orders: reassignOrders,
						reason: reason || undefined,
					}),
				},
			);
			const data = await res.json();
			if (res.ok && data.success) {
				toast.success(data.message || t("messages.success"));
				onOpenChange(false);
				reset();
				router.reload();
			} else {
				toast.error(data.error || t("messages.error"));
			}
		} catch {
			toast.error(t("messages.error"));
		} finally {
			setLoading(false);
		}
	}

	return (
		<Dialog
			open={open}
			onOpenChange={(v) => {
				if (!v) reset();
				onOpenChange(v);
			}}
		>
			<DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
				<DialogHeader>
					<DialogTitle>{t("title")}</DialogTitle>
					<DialogDescription>{t("description")}</DialogDescription>
				</DialogHeader>

				<div className="grid gap-4">
					{/* Overview: where the patient stands right now */}
					<div className="grid gap-2 p-3 border rounded-md bg-muted/40">
						<p className="text-xs font-medium tracking-wide uppercase text-muted-foreground">
							{t("current_owner.title")}
						</p>
						<div className="flex items-start justify-between gap-2">
							<div className="grid gap-0.5">
								<span className="text-sm font-medium">{patientName}</span>
								{patientNumber && (
									<span className="text-xs text-muted-foreground">
										{patientNumber}
									</span>
								)}
							</div>
							<Badge
								variant={hasUserAccount ? "secondary" : "outline"}
								className="gap-1 shrink-0"
							>
								{hasUserAccount ? (
									<UserCheck className="size-3" />
								) : (
									<UserX className="size-3" />
								)}
								{hasUserAccount
									? t("current_owner.has_account")
									: t("current_owner.no_account")}
							</Badge>
						</div>
						<div className="grid gap-0.5 text-xs text-muted-foreground">
							<span>
								{t("current_owner.email_label")}: {patientEmail || "-"}
							</span>
							<span>
								{t("current_owner.orders_label")}: {orderCount}
							</span>
						</div>
					</div>

					{/* Explainer: what this action actually does, adapted to the patient */}
					<Alert>
						<AlertTitle>{t("explainer.title")}</AlertTitle>
						<AlertDescription>
							{hasUserAccount ? t("explainer.transfer") : t("explainer.claim")}
						</AlertDescription>
					</Alert>

					<div className="flex justify-center">
						<ArrowDown className="size-4 text-muted-foreground" />
					</div>

					<div className="grid gap-1.5">
						<Label>{t("fields.user.label")}</Label>
						<AsyncSearchCombobox<UserSearchCandidate>
							fetchUrl="/admin-portal/user-management/search"
							getKey={(u) => u.id}
							placeholder={t("fields.user.placeholder")}
							emptyText={t("empty")}
							onSelect={setSelected}
							renderItem={(u) => (
								<div className="flex flex-col">
									<span className="text-sm">{u.name}</span>
									<span className="text-xs text-muted-foreground">
										{u.email}
									</span>
								</div>
							)}
						/>
						<p className="text-xs text-muted-foreground">
							{t("fields.user.hint")}
						</p>
					</div>

					{selected && (
						<div className="grid gap-0.5 p-3 border rounded-md">
							<p className="text-xs font-medium tracking-wide uppercase text-muted-foreground">
								{t("target_label")}
							</p>
							<span className="text-sm font-medium">{selected.name}</span>
							<span className="text-xs text-muted-foreground">
								{selected.email}
							</span>
						</div>
					)}

					<div className="grid gap-1.5">
						<div className="flex items-center gap-2 text-sm">
							<Checkbox
								id="reassign-orders"
								checked={reassignOrders}
								onCheckedChange={(v) => setReassignOrders(v === true)}
							/>
							<Label htmlFor="reassign-orders">{t("reassign_orders")}</Label>
						</div>
						<p className="text-xs text-muted-foreground">
							{reassignOrders
								? t("reassign_orders_hint", { count: orderCount })
								: t("reassign_orders_hint_off")}
						</p>
					</div>

					<div className="grid gap-1.5">
						<Label htmlFor="change-owner-reason">{t("reason.label")}</Label>
						<Textarea
							id="change-owner-reason"
							rows={3}
							value={reason}
							onChange={(e) => setReason(e.target.value)}
							placeholder={t("reason.placeholder")}
						/>
					</div>
				</div>

				<DialogFooter>
					<Button
						variant="outline"
						onClick={() => onOpenChange(false)}
						disabled={loading}
					>
						{td("modal.cancel")}
					</Button>
					<Button onClick={onSubmit} disabled={loading || !selected}>
						{loading && <LoaderIcon className="mr-2 size-4 animate-spin" />}
						{td("modal.save")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
